import { clsx } from 'clsx';
import type { TicketPriority } from '@centraoneplus/shared-types';

interface PriorityBadgeProps {
  priority: TicketPriority;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /**
   * 'text' (default) is the bare coloured glyph+label used down the list.
   * 'chip' is the bordered form the ticket-detail plate uses, where it sits
   * beside the status badge and needs the same weight.
   */
  variant?: 'text' | 'chip';
  className?: string;
}

/**
 * Ivory & Brass priority badge.
 *
 * Lucide arrows are replaced with typographic glyphs: at 10–12px a stroked
 * arrow icon and a triangle glyph read the same, and the glyph sits on the
 * text baseline with the mono label instead of fighting it. Low/medium are
 * deliberately quiet — only high and urgent earn color, because if every
 * priority is colored then none of them is a signal.
 */
const priorityConfig: Record<
  TicketPriority,
  { label: string; glyph: string; color: string }
> = {
  low: { label: 'Low', glyph: '▽', color: 'text-[var(--co-ink-4)]' },
  medium: { label: 'Medium', glyph: '–', color: 'text-[var(--co-ink-3)]' },
  high: { label: 'High', glyph: '▲', color: 'text-[var(--co-terracotta)]' },
  urgent: { label: 'Urgent', glyph: '◆', color: 'text-[var(--co-burgundy)]' },
};

/** Bordered form: same hue as the text form, with a wash and a 1px edge. */
const chipClasses: Record<TicketPriority, string> = {
  low: 'border-[var(--co-line-strong)] text-[var(--co-ink-4)]',
  medium: 'border-[var(--co-line-strong)] text-[var(--co-ink-3)]',
  high: 'border-[var(--co-terracotta)] bg-[var(--co-terracotta-wash)] text-[var(--co-terracotta)]',
  urgent: 'border-[var(--co-burgundy)] bg-[var(--co-burgundy-wash)] text-[var(--co-burgundy)]',
};

const sizeClasses = {
  sm: 'text-[9px] tracking-[.13em]',
  md: 'text-[10px] tracking-[.13em]',
  lg: 'text-[11px] tracking-[.12em]',
};

/**
 * Chip box, matching StatusBadge's exactly — the two render side by side in the
 * header plate and must be the same height. Kept in step with StatusBadge's
 * `sizeClasses`; if one changes, change both.
 */
const chipBox = {
  sm: 'h-[20px] px-[7px]',
  md: 'h-[24px] px-[9px]',
  lg: 'h-[26px] px-[11px]',
};

export function PriorityBadge({
  priority,
  showLabel = false,
  size = 'md',
  variant = 'text',
  className,
}: PriorityBadgeProps) {
  const config = priorityConfig[priority] || priorityConfig.medium;
  const isChip = variant === 'chip';

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-[5px] font-mono font-semibold uppercase leading-none',
        isChip
          ? clsx('rounded-[2px] border', chipBox[size], chipClasses[priority] ?? chipClasses.medium)
          : config.color,
        sizeClasses[size],
        className,
      )}
      title={config.label}
    >
      <span aria-hidden="true">{config.glyph}</span>
      {showLabel && <span>{config.label}</span>}
      {!showLabel && <span className="sr-only">{config.label} priority</span>}
    </span>
  );
}

export default PriorityBadge;
